import { useQuery } from "@apollo/client/react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Flex, Select, Text } from "orcs-design-system";

import { personAttributeFieldValues as personAttributeFieldValuesQuery } from "src/overlayAttributes/queries/personAttributeFieldValues.graphql";
import { useApplyFilters } from "src/contexts/filterAndFte/FilterAndFteContext";
import { useGradeFieldOptions } from "src/pages/OrgDesignSettings/useGradeFieldOptions";
import {
  ATTRIBUTE_FIELD_PARAM,
  ATTRIBUTE_TEAM_PARAM,
  ATTRIBUTE_VALUE_PARAM,
  TAG_LABEL_PARAM,
  TAG_PARAM,
  parseWorkerAttributePeopleSearch,
} from "src/pages/TeamDetailPage/tabs/TeamInsightsTab/workerAttributePeoplePath";

const bagParts = (
  field: string
): { attributeType: string; subtype: string } | null => {
  const prefix = "custom:";
  if (!field.startsWith(prefix)) {
    return null;
  }
  const rest = field.slice(prefix.length);
  const colon = rest.indexOf(":");
  if (colon <= 0 || colon === rest.length - 1) {
    return null;
  }
  return {
    attributeType: rest.slice(0, colon),
    subtype: rest.slice(colon + 1),
  };
};

const PersonAttributeFilter = () => {
  const history = useHistory();
  const location = useLocation();
  const applyFilters = useApplyFilters();
  const parsed = parseWorkerAttributePeopleSearch(location.search);
  const active = (parsed?.filters ?? []).filter(
    (item): item is { field: string; value: string } =>
      !("kind" in item && item.kind === "tag")
  );
  const first = active[0];
  const field = first?.field ?? "";
  const { options, loading } = useGradeFieldOptions("personAttribute");
  const bag = bagParts(field);
  const valuesQuery = useQuery(personAttributeFieldValuesQuery, {
    variables: {
      attributeType: bag?.attributeType ?? "",
      subtype: bag?.subtype ?? null,
      search: null,
      limit: 50,
    },
    skip: !bag,
  });
  const listed = valuesQuery.data;
  const fromQuery =
    listed &&
    typeof listed === "object" &&
    "values" in listed &&
    Array.isArray(listed.values)
      ? listed.values.filter(
          (value): value is string => typeof value === "string"
        )
      : [];
  const valueOptions = [
    ...(first?.value ? [first.value] : []),
    ...fromQuery,
  ].filter((value, index, all) => all.indexOf(value) === index);

  const write = (nextField: string, nextValue: string) => {
    const params = new URLSearchParams(location.search);
    params.delete(ATTRIBUTE_FIELD_PARAM);
    params.delete(ATTRIBUTE_VALUE_PARAM);
    const filters = [
      ...(nextField ? [{ field: nextField, value: nextValue }] : []),
      ...active.slice(1),
    ];
    for (const filter of filters) {
      if (!filter.field) {
        continue;
      }
      params.append(ATTRIBUTE_FIELD_PARAM, filter.field);
      params.append(ATTRIBUTE_VALUE_PARAM, filter.value);
    }
    const search = params.toString();
    history.push(`${location.pathname}${search ? `?${search}` : ""}`);
  };

  const clear = () => {
    const params = new URLSearchParams(location.search);
    params.delete(ATTRIBUTE_FIELD_PARAM);
    params.delete(ATTRIBUTE_VALUE_PARAM);
    params.delete(ATTRIBUTE_TEAM_PARAM);
    params.delete(TAG_PARAM);
    params.delete(TAG_LABEL_PARAM);
    const search = params.toString();
    history.push(`${location.pathname}${search ? `?${search}` : ""}`);
    applyFilters({ personTags: [] });
  };

  return (
    <Flex
      flexDirection="column"
      gap="s"
      mb="r"
      data-testid="person-attribute-filter"
    >
      <Text fontWeight="2">Person attribute</Text>
      {parsed?.teamId ? (
        <Text color="subText">People in this team and the teams under it.</Text>
      ) : null}
      {active.slice(1).map((filter) => (
        <Text key={`${filter.field}:${filter.value}`} color="subText">
          {filter.field}: {filter.value}
        </Text>
      ))}
      <Select
        label="Field"
        inputId="person-attribute-field"
        options={options}
        isDisabled={loading && options.length === 0}
        placeholder={loading ? "Loading fields" : "Choose a field"}
        value={options.find((option) => option.value === field) ?? null}
        onChange={(option: { value: string } | null) => {
          write(option?.value ?? "", "");
        }}
      />
      <Select
        label="Value"
        inputId="person-attribute-value"
        isDisabled={!field}
        options={valueOptions.map((value) => ({ value, label: value }))}
        placeholder="Choose a value"
        value={first?.value ? { value: first.value, label: first.value } : null}
        onChange={(option: { value: string } | null) => {
          write(field, option?.value ?? "");
        }}
      />
      {parsed ? (
        <Button small variant="ghost" onClick={clear}>
          Clear person attribute
        </Button>
      ) : null}
    </Flex>
  );
};

export default PersonAttributeFilter;
